// @ts-nocheck
import { getApiClient, browserCacheBuster } from '@/lib/api/api-client';
import type { Artwork } from '@/types';
import type { BaseQueryParams, PaginatedResponse, FindNftCollectionsParams, CatalogItem, WhereFilter } from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { artworkTransformer } from '@/lib/transformers/artwork.transformer';
import { CACHE_DURATION, DEFAULT_PAGINATION, ARTWORK_SORT_VALUES, STATUS_VALUES } from './catalog-constants';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
import { logger } from '@/lib/utils/logger';
import { LEAN_POPULATE } from './lean-fields';
import {
  ArtworkSchema,
  safeParseWithLogging,
  parseWithContext,
  validateArray,
  createPaginatedValidator
} from '@/lib/validations/catalog';

export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : DEFAULT_PAGINATION.LIMIT,
    query: params.get('query') || undefined,
    category: params.get('category') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

/**
 * NFT Detail upgrades
 * Now uses the centralized transformer for detail tags
 */
export interface DetailUpgrade {
  tags: string[];
  categories: string[];
  metadata: {
    blockchain?: string;
    year?: number;
    tokenStandard?: string;
    marketplace?: string;
    marketplaceUrl?: string;
    oneOfOne?: boolean;
  };
}

export function getDetailUpgrades(artwork: Artwork): DetailUpgrade {
  const detailTags = artworkTransformer.getDetailTags(artwork);

  // Separate string tags from link tags
  const stringTags = detailTags.filter((t): t is string => typeof t === 'string');
  const linkTags = detailTags.filter((t): t is { title: string; url: string } =>
    typeof t === 'object' && t !== null && 'title' in t && 'url' in t
  );

  // Get categories directly from the transformer
  const categories = artworkTransformer.getCategories(artwork);

  // Extract year from artwork dates
  let year: number | undefined;
  if (artwork.artworkCreatedDate) {
    year = new Date(artwork.artworkCreatedDate).getFullYear();
  } else if (artwork.createdAt) {
    year = new Date(artwork.createdAt).getFullYear();
  }

  // Find marketplace link tag
  const marketplaceLink = linkTags.find(tag =>
    tag.title && ['FOUNDATION', 'OPENSEA', 'SUPERRARE', 'MANIFOLD', 'OBJKT', 'ZORA'].includes(tag.title.toUpperCase())
  );

  return {
    tags: stringTags,
    categories: categories,
    metadata: {
      blockchain: artwork.blockchain && typeof artwork.blockchain !== 'number' ? artwork.blockchain.title : undefined,
      year: year,
      tokenStandard: artwork.standard && typeof artwork.standard !== 'number' ? artwork.standard : undefined,
      marketplace: artwork.marketplace && typeof artwork.marketplace !== 'number' ? artwork.marketplace.title : undefined,
      marketplaceUrl: marketplaceLink?.url || artwork.marketLink || undefined,
      oneOfOne: artwork.oneofone || undefined
    }
  };
}

/**
 * Convert artwork to catalog item
 * Now uses the centralized transformer
 */
export function artworkToCatalogItem(artwork: Artwork): CatalogItem {
  return artworkTransformer.toCatalogItem(artwork);
}

/**
 * Catalog page API service
 */
export class ArtworksAPI {
  static apiClient = getApiClient();

  /**
   * Get meta data for a specific key
   */
  static async findArtworks(
    params: FindNftCollectionsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    try {
      const {
        page = DEFAULT_PAGINATION.PAGE,
        limit = DEFAULT_PAGINATION.LIMIT,
        query,
        category,
        sort,
        years,
        medium,
        platform,
        chains,
        type,
        collectionid,
        oneofone,
        ...rest
      } = params;

      // Special cases: direct sort without featured prefix
      let sorts: string[];
      if (sort === 'sales') {
        sorts = ['-totalSales'];
      } else if (sort === 'topsales') {
        sorts = ['-lastSalePrice'];
      } else {
        // Artworks use 'artworkCreatedDate' for date field
        const sortValue = getSortValue(sort, { dateField: 'artworkCreatedDate', titleField: 'name' });
        sorts = ['featured', sortValue, 'id'];
      }

      // Build where query
      const where: WhereFilter = {};

      // One-of-ones have their own catalog section, so an unscoped listing hides
      // them to avoid presenting the same work twice. Inside a series, though, a
      // one-of-one still belongs to that series and must appear with the rest of
      // it — that is the case this condition protects.
      const collectionScoped =
        Boolean(collectionid && collectionid > 0) ||
        Boolean((rest.where as WhereFilter | undefined)?.collection);

      if (oneofone !== undefined) {
        where.oneofone = {
          equals: oneofone,
        };
      } else if (!collectionScoped) {
        where.oneofone = {
          equals: false,
        };
      }

      where.hidelisting = {
        not_equals: true,
      };

      if (collectionid && collectionid > 0) {
        where.collection = {
          equals: collectionid,
        };
      }

      where['collection.status'] = {
        not_equals: STATUS_VALUES.DRAFT,
      };

      if (query) {
        where.name = {
          contains: query,
        };
      }

      if (years && years.length > 0) {
        try {
          const yearConditions = years.map(year => {
            const startDate = new Date(Number(year), 0, 1).toISOString();
            const endDate = new Date(Number(year), 11, 31, 23, 59, 59).toISOString();

            return {
              and: [
                { artworkCreatedDate: { greater_than_equal: startDate } },
                { artworkCreatedDate: { less_than_equal: endDate } },
              ],
            };
          });

          if (yearConditions.length === 1) {
            Object.assign(where, yearConditions[0]);
          } else {
            where.or = yearConditions;
          }
        } catch (error) {
          // Log error but continue with other filters
          logger.error('Error processing years filter:', error);
        }
      }

      if (medium && medium.length > 0) {
        where['collection.artCategory'] = {
          in: medium,
        };
      }

      if (platform && platform.length > 0) {
        where.marketplace = {
          in: platform,
        };
      }

      if (chains && chains.length > 0) {
        where.blockchain = {
          in: chains,
        };
      }

      if (rest.where) {
        Object.assign(where, rest.where);
      }


      const response = await ArtworksAPI.apiClient.collections.artworks.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        populate: LEAN_POPULATE,
        // TODO: type properly - payload-rest-client sort type doesn't accept comma-joined strings
        sort: sorts.join(',') as never,
        ...browserCacheBuster(),
      });

      const result = response as PaginatedResponse<Artwork>;

      // Validate the paginated response
      if (result.docs && result.docs.length > 0) {
        const PaginatedArtworkSchema = createPaginatedValidator(ArtworkSchema);
        const validationResult = safeParseWithLogging(PaginatedArtworkSchema, result, 'ArtworkService.findArtworks');
        if (!validationResult.success) {
          logger.warn('Paginated artworks validation failed, using raw response:', validationResult.error);
          // Continue with raw response for backward compatibility
        }
      }

      if (result.totalDocs && result.limit) {
        const totalPages = Math.ceil(result.totalDocs / result.limit);
        result.totalPages = totalPages;
        result.hasNextPage = result.page < totalPages;

        const catalogItems = result.docs.map(artworkToCatalogItem);

        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        result.hasNextPage = result.page < result.totalPages;

        return success({
          ...result,
          docs: result.docs.map(artworkToCatalogItem),
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findArtworks', error);
    }
  }

  /**
   * Retrieves artwork by ID
   */
  static async getDetailById(
    id: string | number,
    skipCache: boolean = false
  ): Promise<ApiResult<CatalogItem>> {
    try {
      const cacheKey = await CacheService.scopedKey(`artwork.service_${id}`);

      if (!skipCache) {
        const cachedData = CacheService.getFromCache(cacheKey);
        if (cachedData) {
          return success(cachedData as CatalogItem);
        }
      }

      // Options go at the top level: wrapped in `params` they reached the CMS as params[depth],
      // which it ignores (depth 2 held only because it is the CMS default).
      const response = await ArtworksAPI.apiClient.collections.artworks.findById({
        id: id as number,
        depth: 2,
        populate: LEAN_POPULATE,
      });

      if (response) {
        // Validate the response with Zod
        const validationResult = safeParseWithLogging(ArtworkSchema, response, 'ArtworkService.getDetailById');
        if (!validationResult.success) {
          logger.warn('Artwork validation failed, using raw response:', validationResult.error);
          // Continue with raw response for backward compatibility
        }

        const catalogItem = artworkToCatalogItem(response);
        let artwork = { ...response, ...catalogItem };

        // Get the detail upgrades as a separate concern
        // artwork is a spread of response (Artwork) + catalogItem (CatalogItem)
        const detailUpgrade = getDetailUpgrades(response);

        // Add detailTags to artwork for backward compatibility with components
        // Reconstruct detailTags from the DetailUpgrade structure
        const detailTags: (string | { title: string; url: string })[] = [
          ...detailUpgrade.tags,
          // Add marketplace link if exists
          ...(detailUpgrade.metadata.marketplace && detailUpgrade.metadata.marketplaceUrl
            ? [{ title: detailUpgrade.metadata.marketplace, url: detailUpgrade.metadata.marketplaceUrl }]
            : detailUpgrade.metadata.marketplace
            ? [detailUpgrade.metadata.marketplace]
            : [])
        ];

        artwork = { ...artwork, detailTags };

        CacheService.addToCache(cacheKey, artwork, CACHE_DURATION);
        return success(artwork);
      }

      // If no response, return error
      return handleApiError('getDetailById', new Error('Artwork not found'));
    } catch (error) {
      return handleApiError('getDetailById', error);
    }
  }
}
